<script>
  import { onMount } from 'svelte';
  import SettingsCard from './shared/SettingsCard.svelte';
  import SettingField from './shared/SettingField.svelte';
  import SettingsState from './shared/SettingsState.svelte';
  import {
    loadCameraRecordingSetting,
    normalizeCameraRecordingConfig,
    resetCameraRecordingSetting,
    saveCameraRecordingSetting
  } from '$lib/camera-recording-settings.js';

  const PROFILE_OVERRIDES = {
    standard: {},
    quality: {
      capture: {
        resolution: '1920x1080',
        frameRate: 30,
        audioEnabled: true
      },
      recording: {
        segmentDurationMs: 5000,
        videoBitsPerSecond: 5000000,
        audioBitsPerSecond: 192000
      },
      output: {
        format: 'mp4'
      },
      ffmpeg: {
        preset: 'faster',
        crf: 21
      }
    },
    compact: {
      capture: {
        resolution: '1280x720',
        frameRate: 25,
        audioEnabled: false
      },
      recording: {
        segmentDurationMs: 3000,
        videoBitsPerSecond: 1200000,
        audioBitsPerSecond: 96000
      },
      output: {
        format: 'webm'
      },
      ffmpeg: {
        preset: 'veryfast',
        crf: 30
      }
    }
  };

  const inputClass =
    'h-11 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-900 shadow-sm outline-none transition placeholder:text-slate-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 disabled:cursor-not-allowed disabled:bg-slate-50 disabled:text-slate-500';
  const selectClass =
    'h-11 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-900 shadow-sm outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100 disabled:cursor-not-allowed disabled:bg-slate-50 disabled:text-slate-500';
  const checkboxClass =
    'h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-2 focus:ring-blue-100';
  const actionButtonClass =
    'inline-flex h-10 items-center justify-center rounded-xl px-4 text-sm font-semibold transition focus:outline-none focus:ring-2 focus:ring-blue-100 disabled:cursor-not-allowed disabled:opacity-60';

  let loading = true;
  let saving = false;
  let status = '';
  let error = '';
  let source = 'default';
  let bundle = null;
  let originalConfig = normalizeCameraRecordingConfig();
  let config = normalizeCameraRecordingConfig();

  function clone(value) {
    return JSON.parse(JSON.stringify(value));
  }

  function merge(base, override) {
    if (!override || typeof override !== 'object' || Array.isArray(override)) return override ?? base;

    const result = { ...(base || {}) };
    for (const [key, value] of Object.entries(override)) {
      result[key] = value && typeof value === 'object' && !Array.isArray(value)
        ? merge(base?.[key], value)
        : value;
    }
    return result;
  }

  async function load() {
    loading = true;
    error = '';

    try {
      bundle = await loadCameraRecordingSetting();
      source = bundle.source || 'default';
      originalConfig = clone(bundle.effective);
      config = clone(bundle.effective);
      status = '';
    } catch (err) {
      error = err?.message || 'Không tải được cài đặt camera';
      bundle = {
        defaults: normalizeCameraRecordingConfig(),
        effective: normalizeCameraRecordingConfig()
      };
      originalConfig = clone(bundle.effective);
      config = clone(bundle.effective);
    } finally {
      loading = false;
    }
  }

  onMount(() => {
    load();
  });

  $: profileOptions = bundle?.defaults?.availableProfiles || [];
  $: resolutionOptions = bundle?.defaults?.availableResolutionPresets || [];
  $: frameRateOptions = bundle?.defaults?.availableFrameRates || [];
  $: segmentOptions = bundle?.defaults?.availableSegmentDurations || [];
  $: mimeOptions = bundle?.defaults?.availableMimeTypes || [];
  $: formatOptions = bundle?.defaults?.supportedOutputFormats || [];
  $: ffmpegPresetOptions = bundle?.defaults?.supportedFfmpegPresets || [];
  $: selectedFormat = formatOptions.find(({ id }) => id === config.output.format);
  $: selectedProfile = profileOptions.find(({ id }) => id === config.profile);
  $: isDirty = JSON.stringify(config) !== JSON.stringify(originalConfig);

  function applyProfile(profileId) {
    const overrides = PROFILE_OVERRIDES[profileId] || {};
    config = normalizeCameraRecordingConfig(
      merge(config, {
        profile: profileId,
        ...overrides
      })
    );
    status = '';
  }

  async function save() {
    saving = true;
    error = '';

    try {
      const saved = await saveCameraRecordingSetting(config);
      originalConfig = clone(saved);
      config = clone(saved);
      status = 'Đã lưu cấu hình camera. Phiên camera mới sẽ áp dụng thiết lập này.';
      source = 'user';
    } catch (err) {
      error = err?.message || 'Không thể lưu cài đặt camera';
    } finally {
      saving = false;
    }
  }

  async function reset() {
    saving = true;
    error = '';

    try {
      const nextBundle = await resetCameraRecordingSetting();
      bundle = nextBundle;
      source = nextBundle.source || 'default';
      originalConfig = clone(nextBundle.effective);
      config = clone(nextBundle.effective);
      status = 'Đã đặt lại cài đặt camera về cấu hình mặc định.';
    } catch (err) {
      error = err?.message || 'Không thể đặt lại cài đặt camera';
    } finally {
      saving = false;
    }
  }
</script>

<section class="min-h-full bg-slate-100">
  <div class="mx-auto max-w-7xl space-y-4 p-4 lg:p-6">
    <div class="flex flex-col gap-4 rounded-xl border border-slate-200 bg-white px-5 py-4 shadow-sm sm:flex-row sm:items-start sm:justify-between sm:px-6">
      <div class="min-w-0">
        <h2 class="text-base font-semibold text-slate-900">Cài đặt camera</h2>
        <p class="mt-1 text-sm text-slate-500">Thiết lập mở camera, ghi hình và định dạng xuất file cho pipeline ffmpeg.</p>
      </div>

      <span class="inline-flex w-fit rounded-full bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-700">
        Nguồn cấu hình: {source}
      </span>
    </div>

    {#if loading}
      <SettingsState title="Đang tải cấu hình camera" message="Vui lòng chờ trong giây lát." />
    {:else}
      {#if error}
        <SettingsState type="error" title="Không thể xử lý cấu hình" message={error} />
      {/if}

      {#if status}
        <SettingsState type="success" title="Thành công" message={status} />
      {/if}

      <SettingsCard title="Hồ sơ cấu hình" description="Chọn nhanh cấu hình vận hành phù hợp trước khi tinh chỉnh chi tiết.">
        <div class="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,2fr)_minmax(260px,1fr)]">
          <SettingField id="camera-profile" label="Mẫu cấu hình">
            <select id="camera-profile" value={config.profile} class={selectClass} on:change={(event) => applyProfile(event.currentTarget.value)}>
              {#each profileOptions as option}
                <option value={option.id}>{option.label}</option>
              {/each}
            </select>
          </SettingField>

          <div class="rounded-xl border border-slate-200 bg-slate-50 p-4">
            <p class="text-sm font-semibold text-slate-900">{selectedProfile?.label || 'Tiêu chuẩn'}</p>
            <p class="mt-1 text-sm leading-relaxed text-slate-500">{selectedProfile?.description || ''}</p>
          </div>
        </div>
      </SettingsCard>

      <SettingsCard title="Thiết bị và lấy hình" description="Kiểm soát hướng camera, độ phân giải, tần số khung hình và micro đầu vào.">
        <div class="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
          <SettingField id="camera-facing-mode" label="Camera ưu tiên">
            <select id="camera-facing-mode" bind:value={config.capture.facingMode} class={selectClass}>
              <option value="environment">Camera sau / ngoài</option>
              <option value="user">Camera trước</option>
            </select>
          </SettingField>

          <SettingField id="camera-resolution" label="Độ phân giải">
            <select id="camera-resolution" bind:value={config.capture.resolution} class={selectClass}>
              {#each resolutionOptions as option}
                <option value={option.id}>{option.label} ({option.width}x{option.height})</option>
              {/each}
            </select>
          </SettingField>

          <SettingField id="camera-frame-rate" label="Frame rate">
            <select id="camera-frame-rate" bind:value={config.capture.frameRate} class={selectClass}>
              {#each frameRateOptions as option}
                <option value={option}>{option} fps</option>
              {/each}
            </select>
          </SettingField>

          <label class="flex min-h-11 items-center justify-between gap-3 rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm font-medium text-slate-700 shadow-sm">
            Thu âm từ micro khi quay
            <input type="checkbox" bind:checked={config.capture.audioEnabled} class={checkboxClass} />
          </label>
        </div>
      </SettingsCard>

      <SettingsCard title="Ghi hình" description="Thiết lập segment recorder, mime type ưu tiên và bitrate khi ghi trên trình duyệt.">
        <div class="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
          <SettingField id="recording-segment" label="Độ dài segment">
            <select id="recording-segment" bind:value={config.recording.segmentDurationMs} class={selectClass}>
              {#each segmentOptions as option}
                <option value={option}>{Math.round(option / 1000)} giây</option>
              {/each}
            </select>
          </SettingField>

          <SettingField id="recording-mime" label="MediaRecorder mime type" className="xl:col-span-2">
            <select id="recording-mime" bind:value={config.recording.preferredMimeType} class={selectClass}>
              {#each mimeOptions as option}
                <option value={option.id}>{option.label}</option>
              {/each}
            </select>
          </SettingField>

          <SettingField id="recording-video-bitrate" label="Video bitrate" hint={`${Math.round(config.recording.videoBitsPerSecond / 1000)} Kbps`}>
            <input
              id="recording-video-bitrate"
              type="number"
              min="250000"
              max="20000000"
              step="250000"
              bind:value={config.recording.videoBitsPerSecond}
              class={inputClass} />
          </SettingField>

          <SettingField id="recording-audio-bitrate" label="Audio bitrate" hint={`${Math.round(config.recording.audioBitsPerSecond / 1000)} Kbps`}>
            <input
              id="recording-audio-bitrate"
              type="number"
              min="32000"
              max="512000"
              step="16000"
              bind:value={config.recording.audioBitsPerSecond}
              class={inputClass} />
          </SettingField>
        </div>
      </SettingsCard>

      <SettingsCard title="Xuất file với ffmpeg" description="Chọn định dạng đầu ra, preset encode và quy ước tên file khi server finalize recording.">
        <div class="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
          <SettingField id="output-format" label="Định dạng đầu ra" hint={selectedFormat?.description || ''}>
            <select id="output-format" bind:value={config.output.format} class={selectClass}>
              {#each formatOptions as option}
                <option value={option.id}>{option.label}</option>
              {/each}
            </select>
          </SettingField>

          <SettingField id="ffmpeg-preset" label="FFmpeg preset">
            <select id="ffmpeg-preset" bind:value={config.ffmpeg.preset} class={selectClass}>
              {#each ffmpegPresetOptions as option}
                <option value={option.id}>{option.label}</option>
              {/each}
            </select>
          </SettingField>

          <SettingField id="ffmpeg-crf" label="CRF" hint="Giá trị thấp hơn sẽ cho chất lượng cao hơn.">
            <input id="ffmpeg-crf" type="number" min="18" max="35" step="1" bind:value={config.ffmpeg.crf} class={inputClass} />
          </SettingField>

          <SettingField id="output-file-name" label="Tên file đầu ra" hint={'Dùng biến {date}, {time}, {sessionId}.'} className="xl:col-span-2">
            <input id="output-file-name" type="text" maxlength="120" bind:value={config.output.fileNamePattern} class={inputClass} />
          </SettingField>

          <label class="flex min-h-11 items-center justify-between gap-3 rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm font-medium text-slate-700 shadow-sm">
            Ưu tiên phát ngay (faststart)
            <input type="checkbox" bind:checked={config.ffmpeg.movFlagsFastStart} class={checkboxClass} />
          </label>

          <label class="flex min-h-11 items-center justify-between gap-3 rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm font-medium text-slate-700 shadow-sm">
            Tự gửi vào My Cloud khi hoàn tất
            <input type="checkbox" bind:checked={config.output.shareToCloud} class={checkboxClass} />
          </label>
        </div>
      </SettingsCard>
    {/if}
  </div>

  {#if !loading}
    <footer class="sticky bottom-0 border-t border-slate-200 bg-white/95 px-4 py-3 shadow-sm backdrop-blur">
      <div class="mx-auto flex max-w-7xl flex-col gap-2 sm:flex-row sm:justify-end">
        <button
          class={`${actionButtonClass} border border-slate-300 bg-white text-slate-700 hover:bg-slate-50`}
          type="button"
          on:click={reset}
          disabled={saving}>
          Đặt lại mặc định
        </button>

        <button
          class={`${actionButtonClass} bg-blue-600 text-white shadow-sm hover:bg-blue-700`}
          type="button"
          on:click={save}
          disabled={saving || !isDirty}>
          {saving ? 'Đang lưu...' : 'Lưu cài đặt camera'}
        </button>
      </div>
    </footer>
  {/if}
</section>
